import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { RadioGroup, RadioGroupItem } from "design-system/atoms/RadioGroup"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { UploadS3Input } from "design-system/organisms/UploadS3Input"
import { cn } from "design-system/utils"
import { useUploadBubbleImageMutation } from "modules/campaigns/hooks/useUploadBubbleImageMutation"
import { UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { WidgetConfigurationFormType } from ".."

type WidgetDomainAndPlacementProps = {
  form: UseFormReturn<WidgetConfigurationFormType>
}

export const WidgetDomainAndPlacement = ({ form }: WidgetDomainAndPlacementProps) => {
  const { t } = useTranslation()
  const { bubbleImage } = form.watch()
  const position = form.watch("position")
  const isLeft = position.left != null
  const { mutateAsync: uploadBubbleImage, isPending: isUploadBubbleImagePending } =
    useUploadBubbleImageMutation()

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-x-16 gap-y-4">
        <FormField
          control={form.control}
          name="position.left"
          render={() => (
            <FormItem>
              <FormLabel>
                {t("campaigns.templates.widget.config.domainAndPlacement.side")}
              </FormLabel>
              <FormControl>
                <RadioGroup
                  value={isLeft ? "left" : "right"}
                  onValueChange={(side) => {
                    const sideSpacing = isLeft ? position.left : position.right
                    form.setValue("position.left", side === "left" ? (sideSpacing ?? 0) : null, {
                      shouldDirty: true,
                    })
                    form.setValue("position.right", side === "right" ? (sideSpacing ?? 0) : null, {
                      shouldDirty: true,
                    })
                  }}
                  className="flex w-fit gap-2"
                >
                  {(["left", "right"] as const).map((side) => (
                    <label
                      key={side}
                      htmlFor={`widget-position-${side}`}
                      className={cn(
                        "flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors",
                        (isLeft ? side === "left" : side === "right")
                          ? "border-primary bg-primary/5"
                          : "border-muted-foreground/20 hover:border-muted-foreground/40",
                      )}
                    >
                      <RadioGroupItem id={`widget-position-${side}`} value={side} />
                      <span className="text-sm font-medium leading-snug">
                        {t(`campaigns.templates.widget.config.domainAndPlacement.options.${side}`)}
                      </span>
                    </label>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={isLeft ? "position.left" : "position.right"}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("campaigns.templates.widget.config.domainAndPlacement.sideSpacing")}
              </FormLabel>
              <FormControl>
                <div className="flex items-center gap-2">
                  <Input
                    {...field}
                    value={field.value ?? 0}
                    type="number"
                    wrapperClassName="w-24 shrink-0"
                    {...form.register(field.name, {
                      valueAsNumber: true,
                      validate: (value) => (value ? value > 0 : true),
                    })}
                  />
                  <span>{"px"}</span>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="position.bottom"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("campaigns.templates.widget.config.domainAndPlacement.bottomSpacing")}
              </FormLabel>
              <FormControl>
                <div className="flex items-center gap-2">
                  <Input
                    {...field}
                    type="number"
                    wrapperClassName="w-24 shrink-0"
                    {...form.register(field.name, {
                      valueAsNumber: true,
                      validate: (value) => value > 0,
                    })}
                  />
                  <span>{"px"}</span>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="position.zIndex"
          render={({ field }) => (
            <FormItem className="col-span-1">
              <FormLabel>
                {t("campaigns.templates.widget.config.domainAndPlacement.zIndex")}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="number"
                  wrapperClassName="w-24 shrink-0"
                  {...form.register(field.name, {
                    valueAsNumber: true,
                    validate: (value) => value > 0,
                  })}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div>
        <FormField
          control={form.control}
          name="bubbleImage"
          render={() => (
            <FormItem className="col-span-6 lg:col-span-4">
              <FormLabel>{t("campaigns.templates.widget.config.appearance.bubbleImage")}</FormLabel>
              {!bubbleImage && (
                <FormLabel className="text-muted-foreground">
                  {t("campaigns.templates.widget.config.appearance.bubbleImageDescription")}
                </FormLabel>
              )}
              <FormControl>
                {bubbleImage ? (
                  <div className="flex gap-4 items-center">
                    <div className="w-12 rounded-full overflow-hidden">
                      <img src={bubbleImage} className="" />
                    </div>
                    <Button
                      variant={"outline"}
                      size={"sm"}
                      onClick={() => form.setValue("bubbleImage", null)}
                    >
                      {t("campaigns.templates.widget.config.appearance.removeBubbleImage")}
                    </Button>
                  </div>
                ) : (
                  <UploadS3Input
                    onChange={(media) => {
                      if (media.url) {
                        form.setValue("bubbleImage", media.url)
                      } else {
                        form.setValue("bubbleImage", null)
                      }
                    }}
                    placeholder={t(
                      "campaigns.templates.widget.config.appearance.bubbleImagePlaceholder",
                    )}
                    mutateAsync={uploadBubbleImage}
                    isPending={isUploadBubbleImagePending}
                    disabled={isUploadBubbleImagePending}
                  />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  )
}
